import React, { ChangeEvent, useEffect, useRef, useState } from "react";
import IErrorDTO from "../../../../models/DTO/IErrorDTO";
import ILoadUserDTO from "../../../../models/DTO/ILoadUserDTO";
import IUpdateSupplierIntegrationsDTO from "../../../../models/DTO/IUpdateSupplierIntegrationsDTO";
import EndpointsEnum from "../../../../models/EndpointsEnum";
import INotification from "../../../../models/INotification";
import ISupplierModel from "../../../../models/ISupplierModel";
import IUserUI from "../../../../models/IUserUI";
import NotificationLevelEnum from "../../../../models/NotificationLevelEnum";
import NotificationPositionEnum from "../../../../models/NotificationPositionEnum";
import { NYSLA_DEFAULTS } from "../../../../models/NYSLAConstants";
import { OwAccordion } from "../../../shared/OwAccordion/OwAccordion";
import OwButton from "../../../shared/OwButton/OwButton";
import OwInputWrapper from "../../../shared/OwInputWrapper/OwInputWrapper";
import OwLabel from "../../../shared/OwLabel/OwLabel";
import OwSelectOption from "../../../shared/OwSelectOption/OwSelectOption";
import OwToggle from "../../../shared/OwToggle/OwToggle";
import { Socket } from "socket.io-client";
import TitleTagEnum from "@models/TitleTagEnum";

type Props = {
    userModel: IUserUI;
    supplierModel: ISupplierModel;
    socket: Socket;
    notifyFn: (data: IErrorDTO | null, _notification?: INotification, isFormatted?: boolean) => void;
    isMainViewLight: boolean;
};

// Post type options
const POST_TYPE_OPTIONS = [{ value: "WR", title: "WR - Wholesale/Retail" }];

// Brand registration options
const BRAND_REG_OPTIONS = [
    { value: "P", title: "P - Pending" },
    { value: "R", title: "R - Registered" },
];

// Label type options
const LABEL_TYPE_OPTIONS = [
    { value: "O", title: "O - Original" },
    { value: "P", title: "P - Private" },
];

// Distributor ID options
const DISTRIB_ID_OPTIONS = [
    { value: "N", title: "N - None" },
    { value: "Y", title: "Y - Has Distributor" },
];

export const NYSLAConnect: React.FC<Props> = (props) => {
    const { userModel, supplierModel, socket, notifyFn } = props;
    const integration = supplierModel.Supplier_Integration;

    const [nyslaWholesalerLicense, setNyslaWholesalerLicense] = useState(integration.nysla_wholesaler_license || "");
    const [nyslaDefaultPostType, setNyslaDefaultPostType] = useState(integration.nysla_default_post_type || NYSLA_DEFAULTS.POST_TYPE);
    const [nyslaDefaultBrandReg, setNyslaDefaultBrandReg] = useState(integration.nysla_default_brand_reg || NYSLA_DEFAULTS.BRAND_REG);
    const [nyslaDefaultLabelType, setNyslaDefaultLabelType] = useState(integration.nysla_default_label_type || NYSLA_DEFAULTS.LABEL_TYPE);
    const [nyslaDefaultDistribId, setNyslaDefaultDistribId] = useState(integration.nysla_default_distrib_id || NYSLA_DEFAULTS.DISTRIB_ID);
    const [active, setActive] = useState(false);

    const activeRef = useRef(active);
    activeRef.current = active;

    useEffect(() => {
        const onUpdateSupplierIntegrations = () => {
            setActive(false);
        };

        const notify = () => {
            if (activeRef.current) {
                setActive(false);
                loadUser();
            }
        };

        socket.on(EndpointsEnum.SupplierOnUpdateSupplierIntegrations, onUpdateSupplierIntegrations);
        socket.on(EndpointsEnum.Notify, notify);

        return () => {
            socket.off(EndpointsEnum.SupplierOnUpdateSupplierIntegrations, onUpdateSupplierIntegrations);
            socket.off(EndpointsEnum.Notify, notify);
        };
    }, [socket]);

    const loadUser = () => {
        const payload: ILoadUserDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
        };

        socket.emit(EndpointsEnum.AppLoadUser, payload);
    };

    const toggleNYSLA = () => {
        const payload: IUpdateSupplierIntegrationsDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
            // @ts-expect-error
            Supplier_Integration: {
                has_nysla: !supplierModel.Supplier_Integration.has_nysla,
            },
        };

        socket.emit(EndpointsEnum.SupplierUpdateSupplierIntegrations, payload);
    };

    const handleChange = (e: ChangeEvent<any>) => {
        const value = e.target.type === "checked" ? e.target.checked : e.target.value;
        const name = e.target.name;

        switch (name) {
            case "nysla_wholesaler_license":
                setNyslaWholesalerLicense(value);
                break;
            case "nysla_default_post_type":
                setNyslaDefaultPostType(value);
                break;
            case "nysla_default_brand_reg":
                setNyslaDefaultBrandReg(value);
                break;
            case "nysla_default_label_type":
                setNyslaDefaultLabelType(value);
                break;
            case "nysla_default_distrib_id":
                setNyslaDefaultDistribId(value);
                break;
        }
    };

    const saveChanges = () => {
        const payload: IUpdateSupplierIntegrationsDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
            // @ts-expect-error
            Supplier_Integration: {
                nysla_wholesaler_license: nyslaWholesalerLicense,
                nysla_default_post_type: nyslaDefaultPostType,
                nysla_default_brand_reg: nyslaDefaultBrandReg,
                nysla_default_label_type: nyslaDefaultLabelType,
                nysla_default_distrib_id: nyslaDefaultDistribId,
            },
        };

        socket.emit(EndpointsEnum.SupplierUpdateSupplierIntegrations, payload);

        setActive(true);

        const notification: INotification = {
            message: "nysla_settings_saved",
            level: NotificationLevelEnum.success,
            position: NotificationPositionEnum.bc,
        };
        notifyFn(null, notification);
    };

    const hasNYSLA = !!supplierModel.Supplier_Integration.has_nysla;
    const hasLicense = !!nyslaWholesalerLicense;

    return (
        <div className={"NYSLAConnect"}>
            <OwAccordion
                hideChevron={!hasNYSLA}
                leftIcon={"fad fa-file-certificate"}
                title={"nysla_integration"}
                titleTag={TitleTagEnum.H4}
                titleClick={true}
                rightElem={
                    <div className={"ms-auto"}>
                        <OwToggle fn={toggleNYSLA} name={"has_nysla"} label={""} active={hasNYSLA} />
                    </div>
                }>
                <div className={"d-flex flex-column w-20r"}>
                    {hasNYSLA ? (
                        <div>
                            <div>
                                <OwLabel title={"nysla_wholesaler_license"} />
                                <OwInputWrapper
                                    fn={handleChange}
                                    name={"nysla_wholesaler_license"}
                                    value={nyslaWholesalerLicense}
                                    placeholder={"Enter license number"}
                                />
                                {hasLicense ? (
                                    <OwLabel className={"text-success"} title={"license_configured"} leftIconClass={"fas fa-check"} />
                                ) : (
                                    <OwLabel className={"text-danger"} title={"license_required"} leftIconClass={"fas fa-times"} />
                                )}
                            </div>

                            <div className={"mt-3"}>
                                <OwLabel title={"nysla_default_post_type"} />
                                <OwSelectOption
                                    fn={handleChange}
                                    name={"nysla_default_post_type"}
                                    options={POST_TYPE_OPTIONS}
                                    valueProp={"value"}
                                    titleProp={"title"}
                                    selectedOption={nyslaDefaultPostType}
                                />
                            </div>

                            <div className={"mt-3"}>
                                <OwLabel title={"nysla_default_brand_reg"} />
                                <OwSelectOption
                                    fn={handleChange}
                                    name={"nysla_default_brand_reg"}
                                    options={BRAND_REG_OPTIONS}
                                    valueProp={"value"}
                                    titleProp={"title"}
                                    selectedOption={nyslaDefaultBrandReg}
                                />
                            </div>

                            <div className={"mt-3"}>
                                <OwLabel title={"nysla_default_label_type"} />
                                <OwSelectOption
                                    fn={handleChange}
                                    name={"nysla_default_label_type"}
                                    options={LABEL_TYPE_OPTIONS}
                                    valueProp={"value"}
                                    titleProp={"title"}
                                    selectedOption={nyslaDefaultLabelType}
                                />
                            </div>

                            <div className={"mt-3"}>
                                <OwLabel title={"nysla_default_distrib_id"} />
                                <OwSelectOption
                                    fn={handleChange}
                                    name={"nysla_default_distrib_id"}
                                    options={DISTRIB_ID_OPTIONS}
                                    valueProp={"value"}
                                    titleProp={"title"}
                                    selectedOption={nyslaDefaultDistribId}
                                />
                            </div>

                            <div className={"mt-3"}>
                                <OwButton
                                    title={"save"}
                                    fn={saveChanges}
                                    active={active}
                                    disableOnActive={true}
                                    className={"btn-primary"}
                                />
                            </div>
                        </div>
                    ) : null}
                </div>
            </OwAccordion>
        </div>
    );
};

export default NYSLAConnect;
